import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components/";
import { _DateField, _HTMLEditor, _NumberField, _Select, _Switch, _TextField } from "../../form";
import { _SelectField } from "..";
import { IRouteParam } from "../../pages/PolicyNeutral";

const DisabledFields = ["ProrogheAccessori", "ProrogheDa", "ProrogheImponibile", "ProrogheImposte", "ProroghePremioFinito", "ProroghePremioNetto", "TotaleInteresseLegale"];

interface IPolicyFieldsListProps {
    BondData: any;
    Info: { [I: string]: Dati.CInfoCampo };
    Limits: Dati.Dictionary<number>;
    RiduzioneSommaGarantita: number | undefined;
    OnBlurAmount: () => void;
    OnChangeAmount: (e: string, val: string) => void;
    OnChangeBondData: (e: string, val: unknown) => void;
    OnChangeDerogation: (e: React.FormEvent<any>, tipo: string, verso: string) => void;
    OnChangeDuration: (e: string, val: string | number) => void;
    OnChangePresentationDate: (name: string, val: string | Date) => void;
    OnChangeReleaseDate: (name: string, val: string | Date) => void;
}

export const _PolicyNeutralFieldsList: React.FC<IPolicyFieldsListProps> = ({
    BondData,
    Info,
    Limits,
    RiduzioneSommaGarantita,
    OnBlurAmount,
    OnChangeAmount,
    OnChangeBondData,
    OnChangeDerogation,
    OnChangeDuration,
    OnChangePresentationDate,
    OnChangeReleaseDate
}) => {
    const { ID_Anag, ID_Polizza } = useParams<IRouteParam>();

    const [ForceLegalInterest, setForceLegalInterest] = useState(false);

    const groupInfoByGroupName = (): { [I: string]: Dati.CInfoCampo[] } =>
        Object.values(Info).reduce((result, item) => {
            result[item.Gruppo] = [...(result[item.Gruppo] || []), item];

            return result;
        }, {});

    const openClientDetailsPopup = (name: string) => {
        const clientDetailsPopup: Window | null = window.open("/anagrafica/0?Popup=true", "PopupBeneficiario", "width=1920,height=1080");

        if (clientDetailsPopup != null) {
            clientDetailsPopup.onbeforeunload = () => {
                const popupID_Anag = clientDetailsPopup?.document.getElementById("PopupID_Anag") as HTMLInputElement | null;

                if (popupID_Anag != null && popupID_Anag.value) {
                    OnChangeBondData(name, popupID_Anag.value);
                }
            };
        }
    };

    const getFieldValue = (name: string): any => {
        let val;

        Object.keys(BondData).forEach(key => {
            if (key == name) {
                val = BondData[key];
            }
        });

        return val;
    };

    const renderField = (field: Dati.CInfoCampo) => {
        const val = getFieldValue(field.Campo);
        const isFieldDisabled = DisabledFields.includes(field.Campo);

        switch (field.ID_TipoCampo) {
            case 1: //Intero
            case 2: //Stringa
                return (
                    <_TextField
                        Value={val || ""}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        OnChange={field.Campo == "Durata" ? OnChangeDuration : OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Testo", field.DerogaVerso || "") : undefined}
                    />
                );
            case 3: //Data
                return (
                    <_DateField
                        Value={val}
                        Name={field.Campo}
                        Label={field.Etichetta}
                        OnChange={field.Campo == "DataPresentazione" ? OnChangePresentationDate : field.Campo == "DataSvincolo" ? OnChangeReleaseDate : OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Data", field.DerogaVerso || "") : undefined}
                        Disabled={isFieldDisabled}
                    />
                );
            case 5: //Checkbox
                return (
                    <_Switch
                        Checked={val || false}
                        Name={field.Campo}
                        Label={field.Etichetta}
                        OnChange={OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Data", field.DerogaVerso || "") : undefined}
                    />
                );
            case 7: //Tipo
                return (
                    <Box display="flex">
                        <_SelectField Info={field} Value={val} OnChange={field.Campo == "ProrogheTipo" ? onChangeExtensions : OnChangeBondData} />
                        {(field.Campo == "ID_AnagBeneficiario" || field.Campo == "ID_AnagBeneficiario2") && (
                            <Box display="flex" alignSelf="center">
                                <Tooltip title={Trad.Trad("Nuovo")}>
                                    <IconButton aria-label="new" size="small" color="secondary" onClick={() => openClientDetailsPopup(field.Campo)}>
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                                <IconButton aria-label="see" size="small" component="a" href={`/anagrafica/${val}`} target="_blank">
                                    <SearchIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                );
            case 9: //Importo
                return renderAmountField(field);
            case 11: //Testo
                return (
                    <_TextField
                        multiline
                        rows={4}
                        Value={val || ""}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        OnChange={OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Testo", field.DerogaVerso || "") : undefined}
                    />
                );
            case 10: //Percentuale
                return renderCommissionField(field);
            case 12: //HTML
                return (
                    <_HTMLEditor
                        OnlyOneTableAllowed
                        Value={val}
                        Name={field.Campo}
                        Label={field.Etichetta}
                        OnChange={OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Testo", field.DerogaVerso || "") : undefined}
                    />
                );
            default:
                return `${field.Campo} ${val}`;
        }
    };

    const renderAmountField = (field: Dati.CInfoCampo): React.ReactNode => {
        const val = getFieldValue(field.Campo);
        const isFieldDisabled = DisabledFields.includes(field.Campo);

        switch (field.Campo) {
            case "Imposte":
            case "ProroghePremioNetto":
            case "ProrogheImponibile":
            case "ProrogheImposte":
            case "ProroghePremioFinito":
            case "Imponibile":
            case "DirittiEmissione":
            case "PremioFinito":
            case "PremioNetto":
                return (
                    <_NumberField
                        ProgressBar={Limits[`${field.Campo}MAX`] != undefined}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        Value={val}
                        Max={Limits[`${field.Campo}MAX`]}
                        Min={Limits[`${field.Campo}MIN`]}
                        OnChange={OnChangeAmount}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Importo", field.DerogaVerso || "") : undefined}
                        Disabled={isFieldDisabled}
                        onBlur={OnBlurAmount}
                    />
                );
            case "ImportoComplessivo":
            case "ImportoComplessivoProvvisoria":
            case "ProrogheDirittiEmissione":
                return (
                    <_NumberField
                        ProgressBar={Limits[`${field.Campo}MAX`] != undefined}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        Value={val}
                        Max={Limits[`${field.Campo}MAX`]}
                        Min={Limits[`${field.Campo}MIN`]}
                        OnChange={field.Campo == "ProrogheDirittiEmissione" ? onChangeExtensions : onChangeTotalAmount}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Importo", field.DerogaVerso || "") : undefined}
                    />
                );
            case "ImportoInteresseLegale":
                return (
                    <>
                        {Glob.AzioneAbilitata("ForzaInteresseLegale", Glob.Scrittura) && (
                            <FormControlLabel
                                control={<Checkbox checked={ForceLegalInterest} onChange={e => setForceLegalInterest(e.currentTarget.checked)} name="ForceLegalInterest" />}
                                label={Trad.Trad("Force Legal Interest Amount")}
                            />
                        )}
                        <_NumberField
                            ProgressBar={Limits[`${field.Campo}MAX`] != undefined}
                            Id={field.Campo}
                            Label={field.Etichetta}
                            Value={val}
                            Max={Limits[`${field.Campo}MAX`]}
                            Min={Limits[`${field.Campo}MIN`]}
                            OnChange={onChangeImportoInteresseLegale}
                            OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Importo", field.DerogaVerso || "") : undefined}
                            Disabled={!ForceLegalInterest}
                        />
                    </>
                );
            default:
                return (
                    <_NumberField
                        ProgressBar={Limits[`${field.Campo}MAX`] != undefined}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        Value={val}
                        Max={Limits[`${field.Campo}MAX`]}
                        Min={Limits[`${field.Campo}MIN`]}
                        OnChange={OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Importo", field.DerogaVerso || "") : undefined}
                        Disabled={isFieldDisabled}
                    />
                );
        }
    };
    const renderCommissionField = (field: Dati.CInfoCampo): React.ReactNode => {
        const val = getFieldValue(field.Campo);
        const isFieldDisabled = DisabledFields.includes(field.Campo);

        switch (field.Campo) {
            default:
                return (
                    <_NumberField
                        ProgressBar={Limits[`${field.Campo}MAX`] != undefined}
                        Id={field.Campo}
                        Label={field.Etichetta}
                        Value={val}
                        Max={Limits[`${field.Campo}MAX`]}
                        Min={Limits[`${field.Campo}MIN`]}
                        Adornment="%"
                        OnChange={field.Campo == "Accessori" ? OnChangeAmount : field.Campo == "TassoInteresseLegale" ? onChangeTassoInteresseLegale : OnChangeBondData}
                        OnChangeDerogation={field.Derogabile ? e => OnChangeDerogation(e, "Percentuale", field.DerogaVerso || "") : undefined}
                        Disabled={isFieldDisabled}
                        onBlur={field.Campo == "Accessori" ? OnBlurAmount : undefined}
                    />
                );
        }
    };
    
    const onChangeTassoInteresseLegale = (e: string, val: string) => {
        const importoInteresseLegale = (Number(val) * BondData.ImportoGarantito) / 100;

        OnChangeBondData("ImportoInteresseLegale", importoInteresseLegale);
        OnChangeBondData("TotaleInteresseLegale", importoInteresseLegale + BondData.ImportoGarantito);
        OnChangeBondData(e, val);
        setForceLegalInterest(false);
    };

    const onChangeImportoInteresseLegale = (e: string, val: string) => {
        const totaleInteresseLegale = Number(val) + BondData.ImportoGarantito;

        OnChangeBondData("TotaleInteresseLegale", totaleInteresseLegale);
        OnChangeBondData(e, val);
    };

    const onChangeExtensions = (e: string, val: string) => {
        OnChangeBondData(e, val);
        OnBlurAmount();
    };

    const onChangeTotalAmount = (e: string, val: string) => {
        if (RiduzioneSommaGarantita != undefined) {
            let guaranteedAmount: number = (Number(val) * RiduzioneSommaGarantita) / 100;

            if (guaranteedAmount > Limits["ImportoGarantitoMAX"]) {
                guaranteedAmount = Limits["ImportoGarantitoMAX"];
            }
            OnChangeBondData("ImportoGarantito", guaranteedAmount);
        }
        OnChangeBondData(e, val);
    };

    const groupedInfo: { [I: string]: Dati.CInfoCampo[] } = groupInfoByGroupName();

    return (
        <>
            {Object.values(groupedInfo).map((group: Dati.CInfoCampo[], index: number) => (
                <Grid container spacing={4} key={index}>
                    {group.map((field: Dati.CInfoCampo) => (
                        <Grid item xs={12} md={4} key={field.Campo}>
                            {renderField(field)}
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Divider light />
                    </Grid>
                </Grid>
            ))}
        </>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/PolicyNeutral/PolicyNeutralFieldsList.tsx